<script lang="ts">
import type { RadioGroupItem, RadioGroupProps } from '@nuxt/ui';
import type { SelectFieldOptions } from '#layers/block-engine/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { defineBlock, useBlockInEditorContext, useSelectOptions } from '#layers/block-engine/public';

interface InputRadioBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<RadioGroupProps, 'size' | 'variant' | 'color'> {
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_radio.name',
	description: '$t:blocks.input_radio.description',
	icon: 'lucide:circle-check',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ typeOptions }) => !typeOptions?.multiple ? ['select'] : [] },
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'options',
			label: '$t:blocks.common.props.options',
			block: BuiltinBlockIds.InputRepeater,
			props: {
				schema: [
					{
						name: 'value',
						label: '$t:blocks.common.props.value',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'label',
						label: '$t:blocks.common.props.label',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'icon',
						label: '$t:blocks.common.props.icon',
						block: BuiltinBlockIds.InputIcon,
						nullable: true,
					},
					{
						name: 'color',
						label: '$t:blocks.common.props.color',
						block: BuiltinBlockIds.InputColor,
						nullable: true,
					},
				],
				displayTemplate: '{{label}}',
			},
		},
		{
			name: 'multiple',
			label: '$t:blocks.common.props.multiple',
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { DRadioGroup } from '#components';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

const props = defineProps<InputRadioBlockProps>();
const model = defineModel<any>();

;

const forwarded = reactivePick(props, 'size', 'variant', 'color');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { normalizedOptions: items } = useSelectOptions<RadioGroupItem>(() => props.options);

const { isPreview } = useBlockInEditorContext();
const previewItems = computed<RadioGroupItem[]>(() => Array.from({ length: 3 }, (_, i) => ({
	value: `option-${i + 1}`,
	label: `Option ${i + 1}`,
}) satisfies RadioGroupItem));
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DRadioGroup
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:aria-readonly="readonly"
			:items="isPreview ? previewItems : items"
			:ui="{
				item: props.readonly ? 'pointer-events-none' : '',
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
